import { useEffect, useState } from 'react';

import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

/**
 * Entry point types for the Rovo onboarding experiment.
 *
 * - 'cc-onboarding-aifc-add': User clicked "Add to Confluence" and generated a doc via Rovo (AI-First Create)
 * - 'cc-onboarding-aifc-xflow-add': Jira cross-flow user who clicked "Add to Confluence"
 * - 'cc-onboarding-skip': User selected "Skip" or "Start with a blank page" during onboarding
 * - 'cc-onboarding-xflow-skip': Jira cross-flow user who skipped
 */
export type OnboardingEntryPoint =
	| 'cc-onboarding-aifc-add'
	| 'cc-onboarding-aifc-xflow-add'
	| 'cc-onboarding-skip'
	| 'cc-onboarding-xflow-skip';

const ENTRY_POINT_PARAM = 'onboardingEntryPoint';
const VALID_ENTRY_POINTS: ReadonlySet<string> = new Set<OnboardingEntryPoint>([
	'cc-onboarding-aifc-add',
	'cc-onboarding-aifc-xflow-add',
	'cc-onboarding-skip',
	'cc-onboarding-xflow-skip',
]);

// Module-scope hook that reads the param from React Router's location state — no window access.
const useOnboardingEntryPointParam = createSingleQueryParamHook(ENTRY_POINT_PARAM);

/**
 * Reads and consumes the `onboardingEntryPoint` URL param set by `useCompleteAccountSetup`
 * during the onboarding redirect.
 *
 * This is a one-page-load signal — the param is stripped from the URL on first read
 * via React Router's `history.replace` (through `setQueryParams`) so it doesn't persist
 * across refreshes or appear in bookmarked URLs.
 *
 * Both reading (`useOnboardingEntryPointParam`) and writing (`setQueryParams`) go through
 * React Router's location state, so they share the same source of truth and no `window`
 * access is required.
 *
 * Returns the detected entry point value, or `null` if the user is not in the experiment,
 * the param is absent, or the value is not a recognised entry point.
 */
export function useEntryPointDetection(): OnboardingEntryPoint | null {
	const { setQueryParams } = useRouteActions();
	const rawValue = useOnboardingEntryPointParam();
	const [entryPoint, setEntryPoint] = useState<OnboardingEntryPoint | null>(null);

	useEffect(() => {
		if (!UNSAFE_expValNoExposure('twcg-1387-rovo-in-onboarding-chat', 'isEnabled', false)) {
			return;
		}

		if (!rawValue || !VALID_ENTRY_POINTS.has(rawValue)) {
			return;
		}

		setEntryPoint(rawValue as OnboardingEntryPoint);

		// Strip the param from the URL using React Router's history.replace so that
		// React Router's location stays in sync. Passing null removes the key.
		setQueryParams({ [ENTRY_POINT_PARAM]: null }, true);
	}, [rawValue, setQueryParams]);

	return entryPoint;
}
